import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { setShowMoreMenu } from '../../store/actions/ui/uiActions';
import { shouldShowMoreBeVisible } from '../../store/selectors/ui/uiSelectors';
import {
  getComment,
  getCommentChild,
  getChildAuthor,
  getParentAuthor
} from '../../store/selectors/comments/commentsSelectors';
import {
  likeCommentRequest,
  unlikeCommentRequest,
  likeChildCommentRequest,
  unlikeChildCommentRequest
} from '../../store/actions/comments/commentsActions';
import {
  translateParentRequest,
  translateChildRequest,
  toggleParentTranslation,
  toggleChildTranslation
} from '../../store/actions/comments/translation/translationActions';
import { getCurrentUser } from '../../store/selectors/user/userSelectors';
import { getCommentsContext } from '../../store/selectors/settings/settingsSelectors';
import { isComplaintsEnabled } from '../../utils/windowUtils';

/**
 * @param {Object{
 *  parent_comment_id: Number
 *  root_comment_id: Number
 * }} ancestry
 * @description Outputs a hash of
 * the comment ancestry ids
 * that need to be compared
 */
export const getAncestryValues = ({
  parent_comment_id: replyParentId,
  root_comment_id: rootParentId
}) => ({
  replyParentId,
  rootParentId
});

/**
 *
 * @param {Object {
 *  state: Object
 *  parentId: Number
 *  ancestryValues: Object
 * }}
 * @description Compares the replyParentId with the rootParentId.
 * If they are the same, it means the child is a reply to a parent,
 * so it calls a selector to get the author for the parent comment.
 * If they are different, then the child is a reply to another child,
 * so it calls a selector to get the author for the child comment it's a reply to.
 */
export const getRepliedToName = ({
  state,
  parentId,
  ancestryValues: { replyParentId, rootParentId }
}) => {
  if (replyParentId !== rootParentId) {
    return getChildAuthor(state, parentId, replyParentId);
  }
  return getParentAuthor(state, parentId);
};

const mapStateToProps = (state, ownProps) => {
  const { id, parentId } = ownProps;
  const {
    body,
    content_id: contentId,
    can_delete: canDelete,
    can_edit: canEdit,
    reported_by_me: isReported,
    deleted: isDeleted,
    user,
    created_at: createdAt,
    liked_by_me: likedByMe,
    likes_count: likesCount,
    ancestry = {},
    replyPosted,
    editing: { isEditing = false } = {},
    translation: {
      isTranslated = false,
      loading: isTranslationLoading = false,
      error: translationError = false,
      previousComment
    } = {},
    body_language: commentLanguage,
    mentioned_users: mentionedUsers
  } = parentId
    ? getCommentChild(state, parentId, id)
    : getComment(state, id) || {};
  const {
    name,
    avatar_url: avatarUrl,
    big_avatar_url: bigAvatarUrl,
    id: userId
  } = user || {};
  const { id: currentUserId, userLanguage } = getCurrentUser(state);

  let repliedToName;
  let rootId;

  if (parentId && ancestry) {
    const ancestryValues = getAncestryValues(ancestry);
    repliedToName = getRepliedToName({ state, parentId, ancestryValues });
    rootId = ancestry.root_comment_id;
  }

  const avatar = bigAvatarUrl || avatarUrl;

  const commentDetails = {
    name,
    repliedToName,
    avatar,
    userId
  };

  const isTranslationLoaded = Boolean(previousComment);
  return {
    body,
    id,
    canDelete,
    canReport: isComplaintsEnabled(),
    canEdit,
    commentDetails,
    createdAt,
    contentId,
    isReported,
    isDeleted,
    showMoreVisible: shouldShowMoreBeVisible(state, id),
    likedByMe,
    likesCount,
    replyPosted,
    rootId,
    authorIsUser: currentUserId === userId,
    isEditing,
    commentLanguage,
    userLanguage,
    isTranslationLoading,
    isTranslationLoaded,
    translationError,
    isTranslated,
    mentionedUsers,
    commentsContext: getCommentsContext(state)
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  ...bindActionCreators(
    {
      // Dispatch passed here so that
      // actions can be created
      // from the menu configuration
      setShowMoreMenu,
      likeCommentRequest,
      unlikeCommentRequest,
      likeChildCommentRequest,
      unlikeChildCommentRequest,
      translateParentRequest,
      translateChildRequest,
      toggleParentTranslation,
      toggleChildTranslation
    },
    dispatch
  )
});

const WithCommentsFromStore = (Component) => {
  return connect(mapStateToProps, mapDispatchToProps)(Component);
};

export default WithCommentsFromStore;
